import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { authenticatedRouting } from "@/modules/router/routings/authenticatedRouting";
import { TaskItem } from "@/modules/tasks/components/TaskItem/TaskItem";
import { TaskItemsHeader } from "@/modules/tasks/components/TaskItemsHeader/TaskItemsHeader";
import { ParsedDashboardTasksItem } from "@/modules/tasks/types/types";

import { mapISOStringToDateString } from "../utils/date";

import { TaskDateHeader } from "./TaskDateHeader/TaskDateHeader";

type DashboardToRateTasksListProps = {
  tasks: ParsedDashboardTasksItem[];
  done?: boolean;
  heading: string;
};

const translationNamespace = "pages/DashboardPage";

export const DashboardSection = ({
  tasks,
  done = false,
  heading,
}: DashboardToRateTasksListProps) => {
  const { t } = useTranslation(translationNamespace);

  const todayDate = mapISOStringToDateString(new Date().toISOString());

  const groupedTasks = useMemo(
    () =>
      tasks?.reduce(
        (group: Record<string, Array<ParsedDashboardTasksItem>>, task) => {
          const { date } = task;
          const parsedDate = mapISOStringToDateString(date);
          group[parsedDate] = group[parsedDate] ?? [];
          group[parsedDate].push(task);
          return group;
        },
        {}
      ),
    [tasks]
  );

  return (
    <div>
      <TaskItemsHeader>{heading}</TaskItemsHeader>
      {Object.entries(groupedTasks).map(([date, tasks]) => (
        <div key={date}>
          <TaskDateHeader>
            {date === todayDate ? t("Today") : date}
          </TaskDateHeader>
          {tasks.map((task) => (
            <TaskItem
              key={task.id}
              name={task.name}
              progress={task.progress}
              done={done}
              pointsNumber={task.pointsNumber}
              link={authenticatedRouting.buildUrl("dashboardTaskDetails", {
                params: {
                  id: task.id,
                },
              })}
            />
          ))}
        </div>
      ))}
    </div>
  );
};
